import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import Footer from "../components/Footer";

const EFFECTIVE_DATE = "February 1, 2026";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: (
      <>
        <p>
          These Terms of Service ("Terms") govern your access to and use of the Nexari platform,
          websites, and related services (collectively, the "Service") provided by Nexari ("Nexari",
          "we", "us", or "our"). By creating an account, subscribing to a plan, or otherwise using the
          Service, you agree to be bound by these Terms. If you do not agree, you may not use the Service.
        </p>
        <p>
          If you are using the Service on behalf of a home-care agency, organization, or other entity, you
          represent and warrant that you have the authority to bind that entity to these Terms.
        </p>
      </>
    ),
  },
  {
    id: "service",
    title: "2. The Service",
    body: (
      <>
        <p>
          Nexari Health provides a software-as-a-service ("SaaS") platform for home-care agencies to
          track employee credentials (certifications, licenses, training records, insurance documents,
          background checks), automate renewal reminders, and generate audit-preparation packets for
          regulatory surveys.
        </p>
        <p>
          <strong>Nexari Health is a workforce-compliance platform, not a medical-records system.</strong>{" "}
          The Service is not designed to receive, store, or process protected health information
          ("PHI") as defined under HIPAA, patient identifiers, diagnoses, care plans, or any clinical
          documentation. You agree not to upload or transmit PHI through the Service. Information
          processed is limited to employee/workforce data (names, emails, role assignments, credential
          documents, and similar).
        </p>
      </>
    ),
  },
  {
    id: "accounts",
    title: "3. Accounts & Eligibility",
    body: (
      <>
        <p>
          You must be at least 18 years old and able to form a legally binding contract to use the
          Service. You are responsible for maintaining the confidentiality of your account credentials
          and for all activities that occur under your account. Notify us immediately at the contact
          email below if you suspect unauthorized access.
        </p>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "4. Subscriptions, Trials & Billing",
    body: (
      <>
        <p>
          Paid plans are billed monthly via Stripe, Inc. ("Stripe"), our payment processor. By
          subscribing, you authorize Nexari and Stripe to charge your payment method on a recurring
          basis until you cancel. Pricing is displayed on the Service and may change with notice.
        </p>
        <p>
          We may offer a free trial for new subscriptions. At the end of the trial, your plan will
          automatically convert to a paid subscription at the rate displayed at signup unless you cancel
          before the trial ends.
        </p>
        <p>
          <strong>Refunds.</strong> Except where required by applicable law, all subscription fees are
          non-refundable. You may cancel at any time; cancellation takes effect at the end of the
          current billing period.
        </p>
        <p>
          <strong>Pre-launch / Early Access.</strong> During pre-launch, paid subscriptions may be
          temporarily disabled. Joining the early-access list does not create a contract for paid
          service.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "5. Acceptable Use",
    body: (
      <>
        <p>You agree not to use the Service to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Upload PHI, patient records, care plans, or any data you do not have lawful right to share;</li>
          <li>Violate any applicable law, regulation, or third-party right;</li>
          <li>Reverse-engineer, decompile, or attempt to access the source code of the Service;</li>
          <li>Resell, sublicense, or white-label the Service without our written consent;</li>
          <li>Upload malware, attempt to disrupt the Service, or scrape data at scale;</li>
          <li>Impersonate any person or misrepresent your affiliation with any entity.</li>
        </ul>
        <p>
          We may suspend or terminate accounts that violate these terms, with or without notice.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "6. AI-Generated Content & Document Analysis",
    body: (
      <>
        <p>
          The Service uses third-party large language models (currently provided by OpenAI) to extract,
          summarize, classify, and verify uploaded documents and to generate suggested messages,
          translations, and compliance recommendations.
        </p>
        <p>
          <strong>AI output is provided "as is" and may contain errors.</strong> You are solely
          responsible for reviewing AI-generated output before acting on it. Nexari does not provide
          legal, medical, or compliance advice, and AI output should not be treated as a substitute for
          professional judgment.
        </p>
      </>
    ),
  },
  {
    id: "data",
    title: "7. Your Data",
    body: (
      <>
        <p>
          You retain all rights to the documents, messages, and other content you upload ("Customer
          Content"). You grant Nexari a worldwide, non-exclusive license to host, process, transmit, and
          display Customer Content solely to provide and improve the Service.
        </p>
        <p>
          Our handling of personal data is described in the{" "}
          <Link to="/privacy" className="text-[#2E5C50] underline">Privacy Policy</Link>.
        </p>
      </>
    ),
  },
  {
    id: "third-parties",
    title: "8. Third-Party Services",
    body: (
      <>
        <p>
          The Service integrates with third-party providers, including Stripe (payments) and OpenAI
          (AI). Your use of those services is subject to their respective terms. Nexari is not
          responsible for third-party services or their availability.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    title: "9. Intellectual Property",
    body: (
      <>
        <p>
          The Service, including its software, design, trademarks, and content (excluding Customer
          Content), is owned by Nexari and protected by intellectual-property laws. We grant you a
          limited, non-exclusive, non-transferable license to access and use the Service in accordance
          with these Terms.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    title: "10. Disclaimers",
    body: (
      <>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, WHETHER
          EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE. NEXARI
          DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI OUTPUTS WILL
          BE ACCURATE OR COMPLETE.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "11. Limitation of Liability",
    body: (
      <>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEXARI AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND
          AGENTS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
          DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR
          USE OF THE SERVICE. NEXARI'S TOTAL LIABILITY FOR ANY CLAIM RELATED TO THE SERVICE WILL NOT
          EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID NEXARI IN THE TWELVE (12) MONTHS PRECEDING THE
          CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100).
        </p>
      </>
    ),
  },
  {
    id: "indemnification",
    title: "12. Indemnification",
    body: (
      <>
        <p>
          You agree to defend, indemnify, and hold harmless Nexari from and against any claims,
          liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out
          of or in any way connected to (a) your use of the Service, (b) your Customer Content, or (c)
          your violation of these Terms or any applicable law.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "13. Termination",
    body: (
      <>
        <p>
          You may cancel your subscription at any time from your account billing page. We may suspend or
          terminate your access to the Service if you breach these Terms or if continued provision of
          the Service becomes impracticable. Sections that by their nature should survive termination
          (including ownership, disclaimers, limitations of liability, and dispute resolution) will
          survive.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "14. Governing Law & Dispute Resolution",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the State of Florida, United States, without regard to
          its conflict-of-laws rules. Any dispute arising out of or relating to these Terms or the
          Service will be resolved exclusively in the state or federal courts located in Orange County,
          Florida, and you consent to personal jurisdiction in those courts.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "15. Changes to These Terms",
    body: (
      <>
        <p>
          We may update these Terms from time to time. If we make material changes, we will notify you
          by email or through the Service. Continued use of the Service after the effective date of the
          updated Terms constitutes your acceptance.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "16. Contact",
    body: (
      <>
        <p>
          Questions about these Terms? Contact us at{" "}
          <a href="mailto:nexari@nexarihealth.com" className="text-[#2E5C50] underline">nexari@nexarihealth.com</a>.
        </p>
      </>
    ),
  },
];

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-[#F9F7F4]">
      <nav className="sticky top-0 z-50 bg-[#F9F7F4]/85 backdrop-blur-xl border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="terms-logo-link">
            <div className="w-8 h-8 bg-[#2E5C50] rounded-md flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">Nexari Health</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#2E5C50] hover:underline" data-testid="terms-back-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-14 lg:py-20">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Legal</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight font-light text-[#1C211F]">
          Terms of <span className="font-bold text-[#2E5C50]">Service</span>
        </h1>
        <p className="mt-3 text-sm text-[#57605A]">Effective date: {EFFECTIVE_DATE}</p>

        <div className="mt-10 space-y-10 text-[#1C211F] leading-relaxed">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} data-testid={`terms-section-${s.id}`}>
              <h2 className="font-heading font-semibold text-xl text-[#1C211F]">{s.title}</h2>
              <div className="mt-3 text-sm text-[#3E4641] space-y-3">{s.body}</div>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
